import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { ApprovalWorkflowStep } from '@/types/domain/approval';
import { CheckCircle2, Clock, ShieldAlert, ArrowRight, UserCheck, AlertTriangle, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ApprovalWorkflowTimelineProps {
  workflow: ApprovalWorkflowStep[];
  currentStage: string;
}

export const ApprovalWorkflowTimeline: React.FC<ApprovalWorkflowTimelineProps> = ({
  workflow,
  currentStage,
}) => {
  const getStepIcon = (status: ApprovalWorkflowStep['status']) => {
    switch (status) {
      case 'Completed':
        return <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />;
      case 'Active':
        return <ShieldAlert className="h-5 w-5 text-primary animate-pulse" />;
      case 'Delegated':
        return <UserCheck className="h-5 w-5 text-purple-600 dark:text-purple-400" />;
      case 'Escalated':
        return <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />;
      case 'Rejected':
        return <XCircle className="h-5 w-5 text-rose-600 dark:text-rose-400" />;
      default:
        return <Clock className="h-5 w-5 text-foreground-muted" />;
    }
  };

  return (
    <Card className="border-border rounded-2xl shadow-2xs font-poppins">
      <CardHeader className="pb-3 border-b border-border/60">
        <div className="flex items-center justify-between">
          <CardTitle className="font-poppins text-base font-bold text-foreground flex items-center gap-2">
            <ArrowRight className="h-4 w-4 text-primary" />
            Approval Workflow Chain
          </CardTitle>
          <span className="text-xs font-bold bg-primary/10 text-primary px-2.5 py-1 rounded-full font-poppins">
            Active: {currentStage}
          </span>
        </div>
      </CardHeader>

      <CardContent className="pt-5">
        <div className="relative pl-6 space-y-6 before:absolute before:left-3 before:top-3 before:bottom-3 before:w-0.5 before:bg-border/60">
          {workflow.map((step) => {
            const isCurrent = step.status === 'Active';

            return (
              <div key={step.stepNumber} className="relative group">
                {/* Node Bullet Icon */}
                <div
                  className={cn(
                    'absolute -left-6 top-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-card ring-4 ring-card',
                    isCurrent && 'ring-primary/20 scale-110'
                  )}
                >
                  {getStepIcon(step.status)}
                </div>

                {/* Step Content */}
                <div
                  className={cn(
                    'rounded-xl border p-4 transition-all',
                    isCurrent
                      ? 'border-primary/40 bg-primary/5 shadow-xs'
                      : 'border-border/80 bg-card/60 hover:bg-card'
                  )}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-extrabold px-2 py-0.5 rounded bg-muted text-foreground-secondary">
                        Stage {step.stepNumber}
                      </span>
                      <h4 className="font-poppins text-sm font-bold text-foreground">{step.stageName}</h4>
                    </div>

                    <span
                      className={cn(
                        'text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full w-fit',
                        step.status === 'Completed' && 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
                        step.status === 'Active' && 'bg-primary text-primary-foreground font-bold shadow-2xs',
                        step.status === 'Pending' && 'bg-muted text-foreground-muted',
                        step.status === 'Rejected' && 'bg-rose-500/10 text-rose-700 dark:text-rose-300'
                      )}
                    >
                      {step.status === 'Active' ? 'Requires Approval' : step.status}
                    </span>
                  </div>

                  <div className="mt-2 text-xs space-y-1">
                    <p className="text-foreground-secondary font-medium">
                      Assigned: <strong className="text-foreground">{step.assignedApproverName || step.approverRole}</strong> ({step.approverRole})
                    </p>

                    {step.actionDate && (
                      <p className="text-[11px] text-foreground-muted font-mono">
                        Actioned: {new Date(step.actionDate).toLocaleString()}
                      </p>
                    )}

                    {step.comments && (
                      <div className="mt-2 rounded-lg bg-background p-2.5 border border-border/60 text-xs italic text-foreground-secondary">
                        "{step.comments}"
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
